import { useCallback, useEffect, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { GridState, StateUpdatedEvent } from 'ag-grid-enterprise'

const PREFIX = 'finadeck:gridState:'
// Bump to discard every table's stored state after a change that would otherwise be
// misapplied (new colIds, removed keys, ...).
const SCHEMA_VERSION = 3
const WRITE_DEBOUNCE_MS = 300

// Restored across sessions: column order, width, visibility, pinning and sort.
// Not filters, pagination, or row selection. `version` is ag-Grid's own state version,
// kept so the grid can migrate older serialised state after an ag-Grid upgrade.
const PERSISTED_KEYS: readonly (keyof GridState)[] = [
  'version',
  'columnOrder',
  'columnSizing',
  'columnVisibility',
  'columnPinning',
  'columnGroup',
  'sort'
]

interface StoredGridState {
  // Our storage-schema version (which keys we keep), independent of ag-Grid's state version.
  schemaVersion: number
  state: Partial<GridState>
}

export interface GridStatePersistence {
  /** Pass to `<AgGridReact initialState={...}>` — the saved view, applied once on init. */
  initialState: GridState | undefined
  /** Wire to `<AgGridReact onStateUpdated={...}>` — debounced write of the persisted keys. */
  onStateUpdated: (event: StateUpdatedEvent) => void
}

/**
 * Persists an ag-Grid table's column layout and sort to localStorage so the user
 * returns to the same view. Table-scoped: `tableId` must be unique per table in the app.
 * A falsy `tableId` makes the hook a no-op (nothing read, nothing written).
 *
 * Requirements for a table using this:
 * - Every persisted column MUST have an explicit `colId`. Columns without `field`/`colId`
 *   get generated numeric ids ("0", "1", ...) that shift when the column list changes and
 *   then apply saved widths/order/visibility to the wrong columns.
 * - Do NOT use `flex` on the columns (ColDef or defaultColDef). A restored flex column
 *   makes ag-Grid mis-calculate the layout at init (before the viewport is measured) and
 *   can blank the whole grid. Use a fixed `width` instead. Stripping flex/width from the
 *   stored state does not fix it — the column must not be flex to begin with.
 */
export const useGridStatePersistence = (tableId: string | undefined): GridStatePersistence => {
  const storageKey = tableId ? `${PREFIX}${tableId}` : undefined

  const initialState = useMemo<GridState | undefined>(() => {
    if (!storageKey) return undefined
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return undefined
      const parsed = JSON.parse(raw) as StoredGridState
      if (parsed?.schemaVersion !== SCHEMA_VERSION || !parsed.state) return undefined
      // Required by ag-Grid when initial state carries only a subset of the column-state
      // properties (we omit aggregation / pivot / rowGroup).
      return { ...parsed.state, partialColumnState: true }
    } catch {
      return undefined
    }
  }, [storageKey])

  const persist = useDebouncedCallback((state: GridState) => {
    if (!storageKey) return
    const slim: Partial<GridState> = {}
    PERSISTED_KEYS.forEach(key => {
      if (state[key] !== undefined) {
        ;(slim as Record<string, unknown>)[key] = state[key]
      }
    })
    try {
      localStorage.setItem(storageKey, JSON.stringify({ schemaVersion: SCHEMA_VERSION, state: slim }))
    } catch {
      // Storage full or unavailable (private window) — persistence is best-effort.
    }
  }, WRITE_DEBOUNCE_MS)

  // Flush a pending write when the grid unmounts (e.g. user navigates away right after
  // a column change) — use-debounce drops the trailing call on unmount otherwise.
  useEffect(() => () => persist.flush(), [persist])

  const onStateUpdated = useCallback((event: StateUpdatedEvent) => persist(event.state), [persist])

  return { initialState, onStateUpdated }
}
